import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

export default class extends Controller {
  static values = {
    organizationId: Number
  };

  connect() {
    if (!this.hasOrganizationIdValue) {
      console.warn("Dashboard: No organization ID provided");
      return;
    }

    // Subscribe to organization channel for call updates
    this.subscription = consumer.subscriptions.create(
      {
        channel: "OrganizationChannel",
        organization_id: this.organizationIdValue
      },
      {
        received: (data) => {
          this.handleMessage(data);
        },
        connected: () => {},
        disconnected: () => {}
      }
    );
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  handleMessage(data) {
    switch (data.type) {
    case "call_ended":
    case "call_status_updated":
      this.updateCallStatus(data);
      break;
    case "call_created":
      this.addNewCall(data);
      break;
    }
  }

  updateCallStatus(data) {
    const callId = data.call_id;
    if (!callId) return;

    // Find the call row in the table (could be in admin or agent view)
    const callRow = document.querySelector(`tr[data-call-id="${callId}"]`);
    if (!callRow) {
      // Still update metrics even if call row not found
      if (data.status === "completed" || data.status === "missed") {
        this.updateMetrics();
      }
      return;
    }

    // Update status badge
    const statusCell = callRow.querySelector("[data-call-status]");
    if (statusCell && data.status) {
      const statusBadge = statusCell.querySelector("span");
      if (statusBadge) {
        // Remove old status classes
        statusBadge.className = "inline-flex px-2 text-xs font-semibold leading-5 rounded-full";
        
        // Add new status classes
        if (data.status === "completed") {
          statusBadge.classList.add("text-green-800", "bg-green-100", "dark:bg-green-900/30", "dark:text-green-300");
        } else if (data.status === "failed" || data.status === "missed") {
          statusBadge.classList.add("text-red-800", "bg-red-100", "dark:bg-red-900/30", "dark:text-red-300");
        } else {
          statusBadge.classList.add("text-yellow-800", "bg-yellow-100", "dark:bg-yellow-900/30", "dark:text-yellow-300");
        }
        
        statusBadge.textContent = data.status.charAt(0).toUpperCase() + data.status.slice(1).replace("_", " ");
      }
    }

    // Update duration if provided
    if (data.duration_seconds !== undefined) {
      const durationCell = callRow.querySelector("[data-call-duration]");
      if (durationCell) {
        const minutes = (data.duration_seconds / 60.0).toFixed(1);
        durationCell.textContent = `${minutes}m`;
      }
    }

    // Update ended_at timestamp if provided
    if (data.ended_at) {
      const timeCell = callRow.querySelector("[data-call-time]");
      if (timeCell) {
        // You might want to use a time formatting library here
        const endedDate = new Date(data.ended_at);
        const now = new Date();
        const diffMs = now - endedDate;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) {
          timeCell.textContent = "Just now";
        } else if (diffMins < 60) {
          timeCell.textContent = `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
        } else {
          const diffHours = Math.floor(diffMins / 60);
          timeCell.textContent = `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
        }
      }
    }

    // Update metrics if this is a completed call
    if (data.status === "completed" || data.status === "missed") {
      this.updateMetrics();
    }
  }

  addNewCall(_data) {
    // If a new call is created, we might want to prepend it to the table
    // For now, we'll just update metrics
    this.updateMetrics();
    
    // Optionally reload the recent calls section
    // This could be done via Turbo Streams or by fetching the new call
  }

  updateMetrics() {
    // Update dashboard metrics by making a lightweight request
    // We'll use Turbo to update just the metrics section
    fetch(window.location.pathname, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then(response => response.text())
      .then(html => {
      // Parse and update metrics
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
      
        // Update calls this month
        const callsThisMonth = doc.querySelector("[data-metric='calls_this_month']");
        if (callsThisMonth) {
          const current = document.querySelector("[data-metric='calls_this_month']");
          if (current) current.textContent = callsThisMonth.textContent;
        }
      
        // Update success rate
        const successRate = doc.querySelector("[data-metric='success_rate']");
        if (successRate) {
          const current = document.querySelector("[data-metric='success_rate']");
          if (current) current.textContent = successRate.textContent;
        }
      
        // Update minutes used
        const minutesUsed = doc.querySelector("[data-metric='minutes_used']");
        if (minutesUsed) {
          const current = document.querySelector("[data-metric='minutes_used']");
          if (current) current.textContent = minutesUsed.textContent;
        }
      })
      .catch(error => {
        console.warn("Dashboard: Could not update metrics:", error);
      });
  }
}

